/**
 * EmailVerificationBanner
 * 
 * Banner che appare per i coordinatori che non hanno verificato l'email.
 * Mostra un avviso con opzione di reinviare l'email di verifica.
 * 
 * Posizionare in AppLayout.jsx sopra il contenuto principale.
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Mail, X, AlertTriangle } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import api from '../../services/api'

export default function EmailVerificationBanner() {
  const { user, tenant } = useAuth()
  const [dismissed, setDismissed] = useState(false)
  const [sending, setSending] = useState(false)
  const [sent, setSent] = useState(false)

  // Non mostrare se:
  // - Banner già chiuso in questa sessione
  // - Email già verificata (tenant O utente)
  // - Non è un coordinatore
  // - Dati non ancora caricati
  if (
    dismissed ||
    !tenant ||
    tenant.emailVerified ||
    user?.emailVerified ||
    user?.role !== 'coordinatore'
  ) {
    return null
  }

  const handleResend = async () => {
    if (sending || sent) return
    
    setSending(true)
    try {
      await api.post('/onboarding/resend-verification', {
        email: tenant.contacts?.email || user.email
      })
      setSent(true)
    } catch (error) {
      console.error('Errore reinvio:', error)
    } finally {
      setSending(false)
    }
  }

  const handleDismiss = () => {
    setDismissed(true)
    // Opzionale: salvare in sessionStorage per persistere durante la sessione
    sessionStorage.setItem('emailBannerDismissed', 'true')
  }

  // Controlla se era già stato chiuso in questa sessione
  if (sessionStorage.getItem('emailBannerDismissed') === 'true') {
    return null
  }

  return (
    <div className="bg-amber-50 border-b border-amber-200 dark:bg-amber-900/20 dark:border-amber-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          
          {/* Messaggio */}
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0">
              <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
            </div>
            <p className="text-sm text-amber-800 dark:text-amber-200">
              <span className="font-medium">Email non verificata.</span>
              {' '}
              <span className="hidden sm:inline">
                Verifica la tua email per poter recuperare la password in caso di smarrimento.
              </span>
            </p>
          </div>

          {/* Azioni */}
          <div className="flex items-center space-x-3">
            {sent ? (
              <span className="text-sm text-green-600 dark:text-green-400 font-medium">
                ✓ Email inviata!
              </span>
            ) : (
              <>
                <button
                  onClick={handleResend}
                  disabled={sending}
                  className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md 
                           text-amber-800 bg-amber-100 hover:bg-amber-200 
                           dark:text-amber-200 dark:bg-amber-800/50 dark:hover:bg-amber-800
                           disabled:opacity-50 disabled:cursor-not-allowed
                           transition-colors"
                >
                  <Mail className="h-4 w-4 mr-1.5" />
                  {sending ? 'Invio...' : 'Reinvia email'}
                </button>

                <Link
                  to="/resend-verification"
                  className="text-sm text-amber-700 dark:text-amber-300 hover:underline hidden sm:block"
                >
                  Problemi?
                </Link>
              </>
            )}

            {/* Chiudi */}
            <button
              onClick={handleDismiss}
              className="p-1 rounded-md text-amber-600 hover:bg-amber-100 
                       dark:text-amber-400 dark:hover:bg-amber-800/50
                       transition-colors"
              aria-label="Chiudi banner"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
